// @ts-strict-ignore
import { BaseAuthedApiRequest } from '@/api';
import {
    OrganizationEntity,
    UserPermissions,
    UserGroupId,
    UserGroupsToCommsGroupsMap,
    IntegrationProviderId,
} from '../types';
import { OrgSettingsUpdatable } from './updatable';
import { RunwayUserAction } from '@/helpers/aclHelpers';
import { TimezonePreferenceEnum } from '@/components/userSettings/EditTimezoneSettingsModal';
import { AIProviderType } from '@/typescriptHelpers';

export interface OrgTagRequest {
    tagName: string;
    destinations: string[];
    color: string;
}

export type CreateOrgTagRequest = OrgTagRequest;

export interface UpdateOrgTagRequest extends OrgTagRequest {
    tagId: string;
}

export interface DeleteOrgTagRequest {
    tagId: string;
}

export const UPDATE_ORG_SETTINGS_SUCCESS = 'UPDATE_ORG_SETTINGS_SUCCESS';
export const UPDATE_ORG_USER_SUCCESS = 'UPDATE_ORG_USER_SUCCESS';

export interface BaseUpdateOrgSettingsRequest {
    orgId: string;
}

export interface UpdateMergeAccountsSettingRequest extends BaseUpdateOrgSettingsRequest {
    mergeAccountsWithAllowedDomainsEnabled: boolean;
}

export interface UpdateUserRequest extends BaseUpdateOrgSettingsRequest {
    userId: string;
    groups: UserGroupId[];
    permissions: UserPermissions[];
    appIds: string[];
}

export interface UpdateOrgUserVCSUserNameRequest extends BaseAuthedApiRequest {
    userId: string;
    vcsUserNames: Record<IntegrationProviderId, string>;
}

export interface UpdateOrgUserTimezonePreferenceRequest extends BaseAuthedApiRequest {
    userId: string;
    timezonePreference: TimezonePreferenceEnum;
}

export interface RemoveOrgUserRequest extends BaseUpdateOrgSettingsRequest {
    userId: string;
}

export interface AddOrUpdateWebhookRequest extends BaseUpdateOrgSettingsRequest {
    url: string;
    description?: string;
    eventTypes: string[];
    appIds: string[];
}

export interface AddNewWebhookRequest extends AddOrUpdateWebhookRequest {
    customHeaders: Record<string, string>;
}

export interface UpdateWebhookRequest extends AddOrUpdateWebhookRequest {
    webhookId: string;
    enabled: boolean;
    customHeaders: Record<string, string | null>;
}

export interface DeleteWebhookRequest extends BaseUpdateOrgSettingsRequest {
    webhookId: string;
}

export interface DeleteApiKeyRequest extends BaseUpdateOrgSettingsRequest {
    apiKeyId: string;
}

export interface UpdateAddNewUsersToAppsSettingRequest extends BaseUpdateOrgSettingsRequest {
    addNewUsersToAppsSettingEnabled: boolean;
}

export interface UpdateOrgTimezoneRequest extends BaseUpdateOrgSettingsRequest {
    timezone: string;
}

export interface UpdateAISettingsRequest extends BaseUpdateOrgSettingsRequest {
    aiEnabled?: boolean;
    aiProvider?: AIProviderType;
    byokApiKey?: string | null;
}

export interface UpdateUserGroupsForDirectorySyncGroupRequest extends BaseUpdateOrgSettingsRequest {
    groupId: string;
    userGroups: UserGroupId[];
}

export interface SetDisableDirectorySyncGroupMappingRequest extends BaseUpdateOrgSettingsRequest {
    disabled: boolean;
}

export interface RemoveUserDeviceRequest extends BaseUpdateOrgSettingsRequest {
    deviceIds: string[];
}

export interface CreateCustomGroupRequest extends BaseUpdateOrgSettingsRequest {
    name: string;
    userActions: RunwayUserAction[];
    members: string[];
    appIds: string[];
    grantAdminToGroupUsers: boolean;
    grantItAdminToGroupUsers: boolean;
}

export interface UpdateProviderIdToUserGroupsToCommsGroupsMapRequest extends BaseUpdateOrgSettingsRequest {
    userGroupsToCommsGroupsMap: UserGroupsToCommsGroupsMap;
    providerId: string;
}

export interface UpdateCustomGroupRequest extends BaseUpdateOrgSettingsRequest {
    groupId: UserGroupId;
    name: string;
    userActions: RunwayUserAction[];
    members: string[];
    appIds: string[];
    grantAdminToGroupUsers: boolean;
    grantItAdminToGroupUsers: boolean;
}

export interface DeleteCustomGroupRequest extends BaseUpdateOrgSettingsRequest {
    groupId: UserGroupId;
}

export interface SetDefaultGroupsDisabledRequest extends BaseUpdateOrgSettingsRequest {
    groupIds?: UserGroupId[];
    disabled: boolean;
}

export interface SkippedGroupDisable {
    groupId: UserGroupId;
    name: string;
    reason: string;
}

export interface SetDefaultGroupsDisabledResponse {
    orgEntity: OrganizationEntity;
    skipped: SkippedGroupDisable[];
}

export interface UpdateIntegrationProviderNameRequest extends BaseUpdateOrgSettingsRequest {
    providerId: IntegrationProviderId;
    name: string;
}

type UpdateOrgSettingsRequestData =
    | UpdateMergeAccountsSettingRequest
    | UpdateUserRequest
    | RemoveOrgUserRequest
    | AddNewWebhookRequest
    | UpdateWebhookRequest
    | DeleteWebhookRequest
    | UpdateAddNewUsersToAppsSettingRequest
    | UpdateOrgTimezoneRequest
    | UpdateAISettingsRequest
    | DeleteApiKeyRequest
    | RemoveUserDeviceRequest
    | UpdateOrgUserVCSUserNameRequest
    | CreateCustomGroupRequest
    | UpdateCustomGroupRequest
    | DeleteCustomGroupRequest
    | SetDefaultGroupsDisabledRequest
    | UpdateUserGroupsForDirectorySyncGroupRequest
    | SetDisableDirectorySyncGroupMappingRequest
    | UpdateProviderIdToUserGroupsToCommsGroupsMapRequest
    | UpdateIntegrationProviderNameRequest
    | BaseUpdateOrgSettingsRequest;

export interface UpdateOrgSettingsRequest {
    requestData: UpdateOrgSettingsRequestData;
    id?: number | string; // the identifier for the instance the settings request is being applied to, in case there can be multiple instances
}

export type OrgSettingsResponse = OrganizationEntity;

// Actions

export interface TryUpdateOrgSettingsAction {
    type: OrgSettingsUpdatable;
    payload: UpdateOrgSettingsRequest;
}

export interface UpdateOrgSettingsSuccessAction {
    type: typeof UPDATE_ORG_SETTINGS_SUCCESS;
    payload: OrgSettingsResponse;
}

// Action creators

export const tryUpdateOrgSettings: (
    type: OrgSettingsUpdatable,
    requestData: UpdateOrgSettingsRequestData,
    id?: number | string,
) => TryUpdateOrgSettingsAction = (type, requestData, id) => {
    return {
        type: type,
        payload: {
            requestData,
            id: id ?? null,
        },
    };
};

export const updateOrgSettingsSuccessAction: (payload: OrgSettingsResponse) => UpdateOrgSettingsSuccessAction = (
    payload,
) => {
    return {
        type: UPDATE_ORG_SETTINGS_SUCCESS,
        payload,
    };
};
